// shared/numeric/numeric-maskito.factory.ts

import { inject, Injectable } from '@angular/core';
import { MaskitoOptions, MaskitoPreprocessor } from '@maskito/core';
import { maskitoNumber } from '@maskito/kit';
import { NumericFieldConfig } from './numeric.types';
import { NumericLocaleService } from './numeric-locale.service';

/** Integer-digit ceiling used when a config sets no bounds (`999999999999999`). */
const DEFAULT_MAX_INTEGER_DIGITS = 15;

/**
 * Builds Maskito options for a `NumericFieldConfig` + a locale tag.
 *
 * Design notes:
 *  - Locale-aware separators come from `Intl.NumberFormat.formatToParts` — never
 *    hardcoded. `fr-CA`, for example, uses U+00A0 as the group separator.
 *  - Affixes (currency symbol / `%`) are derived automatically from `config.kind`
 *    + locale: en-US currency renders as `$ …`, fr-CA as `… $`. The currency code itself
 *    comes from `NumericLocaleService` — it is never passed in by callers. Callers can
 *    override the rendered affixes by setting `config.prefix` / `config.suffix`
 *    explicitly (empty string wins too).
 *  - `min`/`max` are NOT forwarded to Maskito as values, but their magnitude caps how
 *    many integer digits the mask accepts, so a field with `max: 999` rejects the
 *    fourth digit instead of accepting `99999` and clamping it later. Values within
 *    that digit count can still overshoot while typing; the directive clamps on blur.
 *  - Business validation stays with `numericConfigValidator`.
 *
 * This factory is a plain service with no directive/DOM coupling so it can be
 * reused (previews, table cell editors, unit tests, etc.).
 */
@Injectable({ providedIn: 'root' })
export class NumericMaskitoFactory {
    private readonly localeService = inject(NumericLocaleService);

    build(config: NumericFieldConfig, locale: string, defaultPrecision: number): MaskitoOptions {
        const { decimalSeparator, groupSeparator } = this.separatorsFor(locale);
        const { prefix, suffix } = this.resolveAffixes(config, locale);

        const scale = this.resolveScale(config, defaultPrecision);
        const allowNegative = config.allowNegative ?? false;
        const useGrouping = config.useGrouping ?? true;

        const min = allowNegative ? undefined : 0;

        const options = maskitoNumber({
            decimalSeparator,
            thousandSeparator: useGrouping ? groupSeparator : '',
            maximumFractionDigits: Math.max(0, scale),
            minimumFractionDigits: 0,
            min,
            prefix,
            postfix: suffix,
        });

        return {
            ...options,
            preprocessors: [
                ...(options.preprocessors ?? []),
                integerDigitLimitPreprocessor(this.maxIntegerDigits(config), decimalSeparator),
            ],
        };
    }

    /** Public so the directive can share the exact same separators when parsing/formatting. */
    separatorsFor(locale: string): { decimalSeparator: string; groupSeparator: string } {
        const parts = new Intl.NumberFormat(locale).formatToParts(12345.6);
        return {
            decimalSeparator: parts.find(p => p.type === 'decimal')?.value ?? '.',
            groupSeparator: parts.find(p => p.type === 'group')?.value ?? ',',
        };
    }

    /**
     * Effective prefix/suffix for the input's text (both Maskito and the directive's
     * format/parse must agree). Explicit `config.prefix`/`config.suffix` win; otherwise
     * we auto-derive for `currency` (locale-positioned symbol) and `percentage` (`%`).
     */
    resolveAffixes(config: NumericFieldConfig, locale: string): { prefix: string; suffix: string } {
        const explicit = {
            prefix: config.prefix,
            suffix: config.suffix,
        };
        if (typeof explicit.prefix === 'string' || typeof explicit.suffix === 'string') {
            return { prefix: explicit.prefix ?? '', suffix: explicit.suffix ?? '' };
        }

        if (config.kind === 'currency') {
            const { symbol, position } = this.localeService.currencyDisplay();
            return position === 'prefix'
                ? { prefix: `${symbol} `, suffix: '' }
                : { prefix: '', suffix: ` ${symbol}` };
        }

        if (config.kind === 'percentage') {
            return { prefix: '', suffix: ' %' };
        }

        return { prefix: '', suffix: '' };
    }

    private resolveScale(config: NumericFieldConfig, defaultPrecision: number): number {
        if (typeof config.scale === 'number') return config.scale;
        if (config.kind === 'integer') return 0;
        return defaultPrecision;
    }

    /** Widest integer-digit count the configured bounds can hold. */
    private maxIntegerDigits(config: NumericFieldConfig): number {
        const digits = [config.min, config.max]
            .filter((bound): bound is number => typeof bound === 'number' && Number.isFinite(bound))
            .map(bound => Math.floor(Math.abs(bound)).toString().length);

        if (!digits.length) return DEFAULT_MAX_INTEGER_DIGITS;
        return Math.min(Math.max(...digits), DEFAULT_MAX_INTEGER_DIGITS);
    }
}

/**
 * Rejects a keystroke once the integer part is full, the way `maxlength` does, instead
 * of accepting extra digits and clamping them on blur. Only digits ahead of the decimal
 * separator are counted, so grouping separators, the minus sign and affixes are free.
 */
function integerDigitLimitPreprocessor(
    maxDigits: number,
    decimalSeparator: string,
): MaskitoPreprocessor {
    return ({ elementState, data }, actionType) => {
        if (actionType !== 'insert' || !data) return { elementState, data };

        const { value, selection } = elementState;
        const [from, to] = selection;
        const next = `${value.slice(0, from)}${data}${value.slice(to)}`;

        if (countIntegerDigits(next, decimalSeparator) <= maxDigits) return { elementState, data };

        // Empty data on a collapsed selection makes Maskito treat the insertion as
        // invalid and `preventDefault` it, leaving value and caret untouched.
        return { elementState: { value, selection: [to, to] }, data: '' };
    };
}

function countIntegerDigits(value: string, decimalSeparator: string): number {
    const integerPart = decimalSeparator ? value.split(decimalSeparator)[0] : value;
    return (integerPart.match(/\d/g) ?? []).length;
}
